import { apiServer, apiServer_v2 } from "common/config";
import { UserBadgesData, NotificationsResponse } from "../types";
import { fetchWithToken } from "./fetchWithToken";
import { resolveGuardrail, throwIfGuardrailRejected } from "./photoGuardrail";

export type FlagUserInput = {
  accessToken: string;
  userId: string;
  image: string;
  notes: string;
};
export async function flagUser({
  accessToken,
  userId,
  image,
  notes,
}: FlagUserInput) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("notes", notes);

  // Only append the image part when one was actually picked. Appending a file
  // with an empty `uri` makes React Native's native multipart builder
  // (RCTHTTPFormDataHelper) crash with an NSRangeException (substringToIndex on
  // a length-0 string), which hard-crashes the app on the no-photo flag path.
  if (image) {
    const file = {
      uri: image,
      type: "image/jpeg",
      name: "image",
    };
    formData.append(`image`, file as any, "image");
  }

  const response = await fetchWithToken(apiServer + "/user/report-issue", {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
    },
  });
  return await response?.json();
}

export async function blockUser({
  accessToken,
  userId,
  notes,
}: {
  accessToken: string;
  userId: string;
  notes: string;
}) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("notes", notes);

  const response = await fetchWithToken(apiServer + "/user/block-user", {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return await response?.json();
}

export async function fetchBadges(accessToken: string): Promise<{
  status: string;
  message: string;
  data: UserBadgesData;
}> {
  const response = await fetchWithToken(apiServer + "/user/sync-badges", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return await response?.json();
}

export async function fetchPublicUserBadges(userId: string): Promise<{
  status: string;
  message: string;
  data: UserBadgesData;
}> {
  const response = await fetch(apiServer + `/user/public-badges/${userId}`);
  return await response.json();
}

export async function postBasicInfo({
  name,
  gender,
  yearOfBirth,
  accessToken,
}: {
  name: string;
  gender: string;
  yearOfBirth: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("gender", gender);
  formData.append("year_of_birth", yearOfBirth);

  const response = await fetchWithToken(
    apiServer + "/user/profile-user-basic",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function postBasicInfoBusiness({
  name,
  jobTitle,
  jobFunction,
  jobDescription,
  accessToken,
}: {
  name: string;
  jobTitle: string;
  jobFunction: string;
  jobDescription: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("job_title", jobTitle);
  formData.append("job_function", jobFunction);
  formData.append("job_description", jobDescription);

  const response = await fetchWithToken(
    apiServer + "/user/profile-user-basic",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function postPrivacySettings({
  key,
  value,
  accessToken,
}: {
  key: string;
  value: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("pref_key", key);
  formData.append("pref_value", value);

  const response = await fetchWithToken(
    apiServer + "/user/update-user-account-preference",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function updatProfilePhoto(photo: string, accessToken: string) {
  const formData = new FormData();
  const fileName = `profile_photo.jpg`;
  const file = {
    uri: photo,
    type: "image/jpeg",
    name: fileName,
  };
  formData.append(`photo`, file as any, fileName);

  const response = await fetchWithToken(apiServer + "/user/profile-photo", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  const data = await response?.json();

  throwIfGuardrailRejected(response, data, ["photo"]);

  return data;
}

export async function updateProfilePhotoDesktop(
  file: File,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("photo", file, file.name); // Use the file directly

  const response = await fetchWithToken(apiServer + "/user/profile-photo", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  return resolveGuardrail(response, ["photo"]);
}

/**
 * A rejected verification. `.message` is what the old `new Error(data.message)`
 * carried, so native is unaffected; `code` lets the web modal tell an incorrect
 * code from an expired or exhausted one.
 */
export class VerifyEmailError extends Error {
  constructor(
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "VerifyEmailError";
    // Restores the prototype chain under ES5 downlevelling, without which
    // `instanceof VerifyEmailError` is false.
    Object.setPrototypeOf(this, VerifyEmailError.prototype);
  }
}

export const VERIFY_EMAIL_CODE_ERRORS = [
  "invalid_code",
  "code_expired",
  "too_many_attempts",
] as const;

export function isVerifyEmailCodeError(
  err: unknown,
): err is VerifyEmailError & {
  code: (typeof VERIFY_EMAIL_CODE_ERRORS)[number];
} {
  return (
    err instanceof VerifyEmailError &&
    VERIFY_EMAIL_CODE_ERRORS.includes(
      err.code as (typeof VERIFY_EMAIL_CODE_ERRORS)[number],
    )
  );
}

export interface ResendVerifyUserEmailResponse {
  status: string;
  message: string;
  data: {
    verification_method: "link" | "code";
    resend_available_in: number;
  };
}

export interface VerifyEmailResponse {
  status: string;
  message: string;
  token: string;
  verified: boolean;
  email: string;
  /** The sign-in payload when `login` was requested and the verification consumed a credential; `[]` otherwise. */
  data?: {
    access_token?: string;
    firstLogin?: number;
    [key: string]: unknown;
  };
}

/** True when the reply carries a session to commit (the backend only mints one for a consuming verification). */
export function hasVerifiedSession(
  r: VerifyEmailResponse | undefined,
): r is VerifyEmailResponse & { data: { access_token: string } } {
  return !!r?.data?.access_token;
}

/**
 * `token` is the 40-char link token or, in code mode, the 6-digit code.
 * `login` asks the backend to sign the user in as well.
 */
export async function verifyUserEmail(
  accessToken: string,
  email: string,
  options?: { login?: boolean },
): Promise<VerifyEmailResponse> {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("token", accessToken);
  if (options?.login) {
    formData.append("login", "1");
  }

  const response = await fetch(apiServer_v2 + "/user/verify-user-email", {
    method: "POST",
    body: formData,
  });
  if (response.status === 403) {
    throw new VerifyEmailError("Try again after few minutes", "rate_limited");
  }
  const data = await response.json();
  if (data.status === "error") {
    throw new VerifyEmailError(data.message, data.code);
  }
  return data;
}

/**
 * Ask for a fresh code or link. The backend answers the same way for every
 * address; the only failures are validation (422) and the rate limit (429).
 */
export async function resendVerifyUserEmail(
  email: string,
): Promise<ResendVerifyUserEmailResponse> {
  const formData = new FormData();
  formData.append("email", email);

  const response = await fetch(
    apiServer_v2 + "/user/resend-verify-user-email",
    {
      method: "POST",
      body: formData,
      // Without it a 422/429 comes back as HTML and json() throws a SyntaxError.
      headers: { Accept: "application/json" },
    },
  );

  if (response.status === 429) {
    throw new VerifyEmailError(
      "Too many attempts. Please wait a few minutes and try again.",
      "rate_limited",
    );
  }

  let data: ResendVerifyUserEmailResponse & { code?: string };
  try {
    data = await response.json();
  } catch {
    throw new VerifyEmailError("Something went wrong. Please try again.");
  }

  if (!response.ok || data.status === "error") {
    throw new VerifyEmailError(
      data.message || "Something went wrong. Please try again.",
      data.code,
    );
  }

  return data;
}

export async function fetchNotifications(
  accessToken: string,
  page: number = 1,
  per_page: number = 20,
  notification_type: "live_alert" | "accessibility_information" | "all" = "all",
  unread_only: "yes" | "no" = "no",
  search?: string,
): Promise<NotificationsResponse> {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
    });

    if (notification_type !== "all") {
      queryParams.append("notification_type", notification_type);
    }

    if (unread_only === "yes") {
      queryParams.append("unread_only", unread_only);
    }

    if (search && search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await fetchWithToken(
      `${apiServer}/user/notifications?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const data = await response?.json();

    // Check if the response has the expected structure
    if (data?.status === "success" && data?.data) {
      return {
        pagination: data.data.pagination || {
          current_page: page,
          last_page: 1,
          per_page: per_page,
          total: 0,
          from: 0,
          to: 0,
        },
        notifications: data.data.notifications || [],
      };
    }

    if (data?.status === "error") {
      throw new Error(data.message || "Failed to fetch notifications");
    }

    // Return empty data with default pagination if request failed
    return {
      pagination: {
        current_page: page,
        last_page: 1,
        per_page: per_page,
        total: 0,
        from: 0,
        to: 0,
      },
      notifications: [],
    };
  } catch (error: any) {
    console.error("Error fetching notifications:", error);
    // Return empty data with default pagination on error
    return {
      pagination: {
        current_page: page,
        last_page: 1,
        per_page: per_page,
        total: 0,
        from: 0,
        to: 0,
      },
      notifications: [],
    };
  }
}

export async function markAllNotificationsAsRead(accessToken: string): Promise<{
  status: string;
  message: string;
  data: any[];
}> {
  const response = await fetchWithToken(
    apiServer + "/user/notifications/read-all",
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return await response?.json();
}

export async function markNotificationAsRead(
  accessToken: string,
  notificationId: string,
): Promise<{
  status: string;
  message: string;
  data: any[];
}> {
  const response = await fetchWithToken(
    apiServer + `/user/notifications/${notificationId}/read`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return await response?.json();
}
